import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserMetadata } from './useUserMetadata';
import { Navigate } from 'react-router-dom';

// Force users to be redirected to unauthorized if they have no role
const FORCE_ROLE_CHECK = true;

const withAuthenticationRequired = (Component: React.ComponentType, allowedRoles: string[] = []) => {
  const ComponentName = Component.displayName || Component.name || 'Component';
  
  return function ProtectedRoute(props: any) {
    const { isAuthenticated, isLoading: auth0Loading, getAccessTokenSilently, user } = useAuth0();
    const { userMetadata, loading: metadataLoading } = useUserMetadata();
    const isLoading = auth0Loading || metadataLoading;

    // Debug logging
    useEffect(() => {
      if (!isLoading && isAuthenticated) {
        console.log(`[${ComponentName}] ======= ROLE CHECK =======`);
        console.log(`[${ComponentName}] Required roles:`, allowedRoles);
        console.log(`[${ComponentName}] User:`, user);
        console.log(`[${ComponentName}] UserMetadata:`, userMetadata);
        
        // Log direct metadata access for debugging
        if (user) {
          const directMetadata = user['https://metadata.congruityhealth.com/user_metadata'];
          console.log(`[${ComponentName}] Direct metadata access:`, directMetadata);
        }
      }
    }, [isLoading, isAuthenticated, user, userMetadata]);

    useEffect(() => {
      const checkTokenExpiration = async () => {
        try {
          await getAccessTokenSilently();
        } catch (error) {
          console.error('Token error:', error);
          window.location.href = '/login';
        }
      };
      checkTokenExpiration();
    }, [getAccessTokenSilently]);

    if (isLoading) {
      return (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'
        }}>
          <div style={{
            border: '16px solid #f3f3f3',
            borderTop: '16px solid #3498db',
            borderRadius: '50%',
            width: '120px',
            height: '120px',
            animation: 'spin 2s linear infinite'
          }}/>
        </div>
      );
    }

    if (!isAuthenticated) {
      console.log(`[${ComponentName}] Not authenticated - redirecting to login`);
      return <Navigate to="/login" />;
    }

    // Get roles from different possible sources
    const rolesFromMetadata = Array.isArray(userMetadata?.Roles) ? userMetadata.Roles : [];
    const rolesFromNamespace = user?.['https://metadata.congruityhealth.com/user_metadata']?.Roles || [];
    
    // Combine all possible role sources
    const userRoles = [...rolesFromMetadata, ...rolesFromNamespace];
    
    console.log(`[${ComponentName}] Roles from metadata:`, rolesFromMetadata);
    console.log(`[${ComponentName}] Roles from namespace:`, rolesFromNamespace);
    console.log(`[${ComponentName}] Combined roles:`, userRoles);
    
    // Force checking for roles if enabled
    if (FORCE_ROLE_CHECK && allowedRoles.length > 0) {
      // If user has no roles at all, deny access
      if (userRoles.length === 0) {
        console.log(`[${ComponentName}] No roles found - redirecting to Unauthorized`);
        return <Navigate to="/Unauthorized" />;
      }
      
      // Check each role (case insensitive)
      const hasMatchingRole = allowedRoles.some(allowedRole => 
        userRoles.some(userRole => 
          userRole.toLowerCase() === allowedRole.toLowerCase()
        )
      );
      
      if (!hasMatchingRole) {
        console.log(`[${ComponentName}] No matching role found - redirecting to Unauthorized`);
        console.log(`[${ComponentName}] User roles:`, userRoles);
        console.log(`[${ComponentName}] Allowed roles:`, allowedRoles);
        return <Navigate to="/Unauthorized" />;
      }
      
      console.log(`[${ComponentName}] Role check passed - rendering protected component`);
    } else {
      console.log(`[${ComponentName}] Role check bypassed or no roles required`);
    }

    return <Component {...props} />;
  };
};

export default withAuthenticationRequired;